import { AtSign, Bold, CalendarDays, Heading, Italic, Link2, List, ListOrdered, Strikethrough, Target, Type, Underline } from 'lucide-react';
import { type ReactNode, type Ref, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import { formatDateFull, todayISO } from '../../lib/dates';
import { matchesSearch } from '../../lib/format';
import { modKey } from '../../lib/platform';
import { Avatar } from './Avatar';
import { Kbd } from './Badge';
import { useEscapeLayer } from './escape';
import { Input } from './Field';
import type { Rect } from './layer';
import { Popover } from './Popover';

/*
 * Легкий редактор тексту з форматуванням без сторонніх бібліотек.
 * Формат зберігання — підмножина Markdown: абзаци, заголовки «## » і «### », **жирний**, *курсив*,
 * ++підкреслений++, ~~закреслений~~, [посилання](https://…), списки «- » та «1. », згадки @Ім'я
 * (без ідентифікаторів у тексті: їх цифри сервер міг би сприйняти за чутливі дані).
 * Перетворення в обидва боки екранують текст і пропускають лише білий список тегів і безпечні адреси.
 * Вставка з інших джерел (Asana, Google Документи, Word, пошта) проходить той самий шлях: HTML з буфера →
 * Markdown → безпечний HTML, тож структура (абзаци, списки, заголовки, виділення, посилання) зберігається,
 * а стилі, скрипти й зображення — ні.
 */

export type MentionUser = { id: string; name: string; avatarUrl?: string | null };

const HTML_ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => HTML_ESC[c] ?? c);
const SAFE_URL = /^(https?:\/\/|mailto:)/i;

// ——— Markdown → HTML ———

function inlineToHtml(text: string, names: string[]): string {
  let out = '';
  let i = 0;
  while (i < text.length) {
    const rest = text.slice(i);
    const ch = text.charAt(i);
    if (ch === '\\' && i + 1 < text.length && /[\\*_[\]()@`~+#]/.test(text.charAt(i + 1))) {
      out += esc(text.charAt(i + 1));
      i += 2;
      continue;
    }
    let m = /^\*\*(?=\S)([\s\S]+?)(?<=\S)\*\*/.exec(rest);
    if (m) {
      out += `<strong>${inlineToHtml(m[1] ?? '', names)}</strong>`;
      i += m[0].length;
      continue;
    }
    m = /^([*_])(?=\S)([\s\S]+?)(?<=\S)\1/.exec(rest);
    if (m && !(m[1] === '_' && /\w/.test(text.charAt(i - 1)))) {
      out += `<em>${inlineToHtml(m[2] ?? '', names)}</em>`;
      i += m[0].length;
      continue;
    }
    m = /^~~(?=\S)([\s\S]+?)(?<=\S)~~/.exec(rest);
    if (m) {
      out += `<s>${inlineToHtml(m[1] ?? '', names)}</s>`;
      i += m[0].length;
      continue;
    }
    m = /^\+\+(?=\S)([\s\S]+?)(?<=\S)\+\+/.exec(rest);
    if (m) {
      out += `<u>${inlineToHtml(m[1] ?? '', names)}</u>`;
      i += m[0].length;
      continue;
    }
    m = /^\[([^\]\n]+)\]\(([^)\s]+)\)/.exec(rest);
    if (m && SAFE_URL.test(m[2] ?? '')) {
      out += `<a href="${esc(m[2] ?? '')}" target="_blank" rel="noopener noreferrer">${inlineToHtml(m[1] ?? '', names)}</a>`;
      i += m[0].length;
      continue;
    }
    m = /^https?:\/\/[^\s<>"]+[^\s<>".,;:!?)\]]/.exec(rest);
    if (m && !/[\w/]/.test(text.charAt(i - 1))) {
      out += `<a href="${esc(m[0])}" target="_blank" rel="noopener noreferrer">${esc(m[0])}</a>`;
      i += m[0].length;
      continue;
    }
    if (ch === '@' && !/\w/.test(text.charAt(i - 1))) {
      const name = names.find((n) => rest.startsWith(`@${n}`) && !/[\p{L}\p{N}]/u.test(rest.charAt(n.length + 1)));
      if (name) {
        out += `<span class="rt-mention" contenteditable="false" data-mention="${esc(name)}">@${esc(name)}</span>`;
        i += name.length + 1;
        continue;
      }
    }
    out += ch === '\n' ? '<br>' : esc(ch);
    i += 1;
  }
  return out;
}

const BULLET = /^\s*[-*•]\s+/;
const ORDERED = /^\s*\d{1,3}[.)]\s+/;
/** Заголовок: «## » — заголовок, «### » — підзаголовок («# » читається як заголовок). */
const HEADING = /^(#{1,3})\s+(\S.*)$/;

/** Markdown → безпечний HTML (для перегляду й редактора). `names` — імена для підсвічування згадок. */
export function markdownToHtml(md: string, names: string[] = []): string {
  const sorted = [...names].filter(Boolean).sort((a, b) => b.length - a.length);
  const lines = md.replace(/\r\n?/g, '\n').split('\n');
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (!line.trim()) {
      i += 1;
      continue;
    }
    const h = HEADING.exec(line);
    if (h) {
      // У картці назва задачі — h2, назви розділів — h3; заголовки всередині тексту йдуть рівнем нижче.
      const tag = (h[1] ?? '').length >= 3 ? 'h5' : 'h4';
      out.push(`<${tag}>${inlineToHtml(h[2] ?? '', sorted)}</${tag}>`);
      i += 1;
      continue;
    }
    if (BULLET.test(line) || ORDERED.test(line)) {
      const ordered = ORDERED.test(line);
      const re = ordered ? ORDERED : BULLET;
      const items: string[] = [];
      while (i < lines.length && re.test(lines[i] ?? '')) {
        items.push(`<li>${inlineToHtml((lines[i] ?? '').replace(re, ''), sorted)}</li>`);
        i += 1;
      }
      out.push(ordered ? `<ol>${items.join('')}</ol>` : `<ul>${items.join('')}</ul>`);
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && (lines[i] ?? '').trim() && !BULLET.test(lines[i] ?? '') && !ORDERED.test(lines[i] ?? '') && !HEADING.test(lines[i] ?? '')) {
      para.push(lines[i] ?? '');
      i += 1;
    }
    out.push(`<p>${inlineToHtml(para.join('\n'), sorted)}</p>`);
  }
  return out.join('');
}

// ——— HTML → Markdown ———

const escMd = (s: string) =>
  s
    .replace(/([\\*_[\]])/g, '\\$1')
    .replace(/~~/g, '\\~\\~')
    .replace(/\+\+/g, '\\+\\+');

function wrap(marker: string, inner: string) {
  const lead = inner.match(/^\s*/)?.[0] ?? '';
  const trail = inner.match(/\s*$/)?.[0] ?? '';
  const core = inner.trim();
  return core ? `${lead}${marker}${core}${marker}${trail}` : inner;
}

/** Вага шрифту зі стилю: «bold», «700» → жирний; «normal», «400» → звичайний; порожньо — не задано. */
function styleWeight(el: HTMLElement): 'bold' | 'normal' | null {
  const w = el.style?.fontWeight;
  if (!w) return null;
  if (w === 'bold' || w === 'bolder' || Number(w) >= 600) return 'bold';
  return 'normal';
}

function inlineMd(node: Node, inLink = false): string {
  if (node.nodeType === Node.TEXT_NODE) return escMd((node.textContent ?? '').replace(/\u00a0/g, ' '));
  if (!(node instanceof HTMLElement)) return '';
  const tag = node.tagName;
  if (tag === 'BR') return '\n';
  if (SKIP.has(tag)) return '';
  if (node.dataset.mention) return `@${node.dataset.mention}`;
  if (tag === 'A') {
    const href = node.getAttribute('href') ?? '';
    const inner = Array.from(node.childNodes)
      .map((c) => inlineMd(c, true))
      .join('');
    if (!SAFE_URL.test(href)) return inner;
    const text = node.textContent ?? '';
    return text === href ? href : `[${inner || escMd(href)}](${href})`;
  }
  let inner = Array.from(node.childNodes)
    .map((c) => inlineMd(c, inLink))
    .join('');
  const st = node.style;
  const weight = styleWeight(node);
  // Google Документи обгортають увесь вставлений текст у <b style="font-weight:normal"> — це не жирний.
  const bold = weight === 'bold' || ((tag === 'STRONG' || tag === 'B') && weight !== 'normal');
  const italic = tag === 'EM' || tag === 'I' || st?.fontStyle === 'italic';
  const deco = `${st?.textDecoration ?? ''} ${st?.textDecorationLine ?? ''}`;
  const strike = tag === 'S' || tag === 'DEL' || tag === 'STRIKE' || deco.includes('line-through');
  // Підкреслення всередині посилання — це вигляд посилання, а не форматування.
  const underline = !inLink && (tag === 'U' || tag === 'INS' || deco.includes('underline'));
  if (underline) inner = wrap('++', inner);
  if (strike) inner = wrap('~~', inner);
  if (italic) inner = wrap('*', inner);
  if (bold) inner = wrap('**', inner);
  return inner;
}

/** Теги, уміст яких не є текстом документа (стилі, скрипти, службові вставки Word, зображення). */
const SKIP = new Set(['SCRIPT', 'STYLE', 'META', 'LINK', 'TITLE', 'HEAD', 'SVG', 'IMG', 'PICTURE', 'VIDEO', 'AUDIO', 'IFRAME', 'OBJECT', 'TEMPLATE', 'NOSCRIPT', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA']);
const BLOCK = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'UL', 'OL', 'LI', 'PRE', 'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TD', 'TH', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'MAIN', 'ASIDE', 'NAV', 'DL', 'DT', 'DD', 'FIGURE', 'FIGCAPTION', 'HR']);
const BLOCK_SELECTOR = 'p,div,h1,h2,h3,h4,h5,h6,blockquote,ul,ol,li,pre,table,section,article';
const LIST_LINE = /^(?:- |\d{1,3}\. )/;

const oneLine = (s: string) => s.replace(/\s*\n+\s*/g, ' ').trim();

/** Рядок списку приєднується до попереднього блоку, якщо той теж список: інакше кожен пункт став би окремим списком. */
function pushListLines(out: string[], lines: string[]) {
  if (!lines.length) return;
  const last = out[out.length - 1];
  const sameKind = last !== undefined && LIST_LINE.test(last.split('\n').pop() ?? '') && /^- /.test(last.split('\n').pop() ?? '') === /^- /.test(lines[0] ?? '');
  if (sameKind) out[out.length - 1] = `${last}\n${lines.join('\n')}`;
  else out.push(lines.join('\n'));
}

/** Пункти списку; вкладені списки розгортаються наступними пунктами (у форматі зберігання вкладеності немає). */
function listLines(list: HTMLElement, lines: string[] = []): string[] {
  const ordered = list.tagName === 'OL';
  let n = 0;
  for (const li of Array.from(list.children) as HTMLElement[]) {
    if (li.tagName === 'UL' || li.tagName === 'OL') {
      listLines(li, lines);
      continue;
    }
    if (li.tagName !== 'LI') continue;
    // Списки, вкладені саме в цей пункт (а не глибше): їхні пункти йдуть одразу після нього.
    const nested = Array.from(li.querySelectorAll<HTMLElement>('ul,ol')).filter((l) => l.parentElement?.closest('li') === li);
    const own = li.cloneNode(true) as HTMLElement;
    for (const l of Array.from(own.querySelectorAll('ul,ol'))) l.remove();
    const text = oneLine(
      Array.from(own.childNodes)
        .map((c) => inlineMd(c))
        .join(''),
    );
    n += 1;
    if (text) lines.push(`${ordered ? `${n}.` : '-'} ${text}`);
    for (const sub of nested) listLines(sub, lines);
  }
  return lines;
}

/** Текст блоку без вкладених списків (вони обробляються окремо). */
function inlineOfBlock(el: HTMLElement): string {
  return Array.from(el.childNodes)
    .map((c) => (c instanceof HTMLElement && (c.tagName === 'UL' || c.tagName === 'OL') ? '' : inlineMd(c)))
    .join('');
}

/** Абзац-пункт списку з Word: <p class="MsoListParagraph…"> з маркером у <span style="mso-list:Ignore">. */
function wordListLine(p: HTMLElement): string | null {
  if (!/MsoList/i.test(p.className) && !/mso-list/i.test(p.getAttribute('style') ?? '')) return null;
  const clone = p.cloneNode(true) as HTMLElement;
  let marker = '';
  for (const span of Array.from(clone.querySelectorAll<HTMLElement>('span'))) {
    if (/mso-list\s*:\s*ignore/i.test(span.getAttribute('style') ?? '')) {
      marker += span.textContent ?? '';
      span.remove();
    }
  }
  const text = oneLine(Array.from(clone.childNodes).map((c) => inlineMd(c)).join(''));
  if (!text) return null;
  const num = /^\s*(\d{1,3})[.)]/.exec(marker);
  if (num) return `${num[1]}. ${text}`;
  // Маркер без позначки «mso-list:Ignore» лишається в тексті: номер зберігаємо, значок маркера прибираємо.
  const inText = /^(\d{1,3})[.)]\s+(?=\S)/.exec(text);
  return inText ? `${inText[1]}. ${text.slice(inText[0].length)}` : `- ${text.replace(/^[·•o§▪–-]\s+/, '')}`;
}

function blocksMd(root: Node, out: string[]) {
  let inline = '';
  const flush = () => {
    const t = inline.replace(/\n+$/, '');
    if (t.trim()) out.push(escapeHeading(t));
    inline = '';
  };
  for (const child of Array.from(root.childNodes)) {
    if (!(child instanceof HTMLElement)) {
      inline += inlineMd(child);
      continue;
    }
    const tag = child.tagName;
    if (SKIP.has(tag)) continue;
    // Обгортка без власного блокового змісту (наприклад, <b id="docs-internal-guid…"> з абзацами всередині).
    const container = !BLOCK.has(tag) && !!child.querySelector(BLOCK_SELECTOR);
    if (!BLOCK.has(tag) && !container) {
      inline += inlineMd(child);
      continue;
    }
    flush();
    if (tag === 'UL' || tag === 'OL') pushListLines(out, listLines(child));
    else if (tag === 'TABLE') {
      // Таблиця — рядками «клітинка | клітинка»: у форматі зберігання таблиць немає, але зміст і порядок лишаються.
      const rows = Array.from(child.querySelectorAll('tr'))
        .map((tr) =>
          Array.from(tr.children)
            .filter((c) => c.tagName === 'TD' || c.tagName === 'TH')
            .map((c) => oneLine(inlineOfBlock(c as HTMLElement) || (c.textContent ?? '')))
            .filter(Boolean)
            .join(' | '),
        )
        .filter(Boolean);
      if (rows.length) out.push(rows.join('\n'));
    } else if (/^H[1-6]$/.test(tag)) {
      const t = oneLine(Array.from(child.childNodes).map((c) => inlineMd(c)).join('')).replace(/^\*\*(.+)\*\*$/, '$1');
      if (t) out.push(`${tag === 'H5' || tag === 'H6' ? '###' : '##'} ${t}`);
    } else if (tag === 'HR') continue;
    else {
      const word = tag === 'P' ? wordListLine(child) : null;
      if (word) {
        pushListLines(out, [word]);
        continue;
      }
      const hasBlocks = container || Array.from(child.children).some((c) => BLOCK.has(c.tagName) || !!c.querySelector(BLOCK_SELECTOR));
      if (hasBlocks) blocksMd(child, out);
      else {
        const t = Array.from(child.childNodes).map((c) => inlineMd(c)).join('').replace(/\n+$/, '');
        if (t.trim()) out.push(escapeHeading(t));
      }
    }
  }
  flush();
}

/** Звичайний абзац, що починається з «#», не має стати заголовком після збереження. */
const escapeHeading = (t: string) => t.replace(/^(\s*)(#{1,6}\s)/, '$1\\$2');

/** Вміст редактора → Markdown (абзаци через порожній рядок). */
export function htmlToMarkdown(root: HTMLElement): string {
  const out: string[] = [];
  blocksMd(root, out);
  return out.join('\n\n').trim();
}

/**
 * HTML з буфера обміну → Markdown. Документ розбирається без виконання (DOMParser), далі проходить той самий
 * білий список, що й уміст редактора; порожній результат — вставляти як звичайний текст.
 */
export function pastedHtmlToMarkdown(html: string): string {
  if (typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return htmlToMarkdown(doc.body);
}

// ——— Перегляд ———

export function RichTextView({ markdown, names, className }: { markdown: string; names?: string[]; className?: string }) {
  const html = useMemo(() => markdownToHtml(markdown, names), [markdown, names]);
  return <div className={cn('rt prose-ers text-body text-fg', className)} dangerouslySetInnerHTML={{ __html: html }} />;
}

// ——— Редактор ———

type Trigger = { kind: 'mention' | 'command'; query: string; rect: Rect; range: Range };

type Command = { id: string; label: string; hint?: string; icon: ReactNode; run: () => void };

function caretRect(range: Range, fallback: HTMLElement): Rect {
  const rects = range.getClientRects();
  const r = rects.length ? rects[rects.length - 1] : range.getBoundingClientRect();
  if (r && (r.width || r.height)) return { top: r.top, left: r.left, bottom: r.bottom, right: r.right, width: r.width, height: r.height };
  const f = fallback.getBoundingClientRect();
  return { top: f.top, left: f.left, bottom: f.top + 20, right: f.left, width: 0, height: 20 };
}

/** Текст перед курсором у поточному текстовому вузлі: для «@ім'я» і «/команда». */
function detectTrigger(editor: HTMLElement): Trigger | null {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE || !editor.contains(node)) return null;
  const before = (node.textContent ?? '').slice(0, range.startOffset);
  const m = /(^|[\s ])([@/])([^\s@/]{0,30})$/.exec(before);
  if (!m) return null;
  const query = m[3] ?? '';
  const start = range.startOffset - query.length - 1;
  const r = document.createRange();
  r.setStart(node, start);
  r.setEnd(node, range.startOffset);
  return { kind: m[2] === '@' ? 'mention' : 'command', query, rect: caretRect(r, editor), range: r };
}

/** Керування редактором ззовні — для поля коментаря, де текст надсилає кнопка, а не автозбереження. */
export type RichTextHandle = {
  /** Поточний текст у форматі зберігання. */
  getMarkdown: () => string;
  /** Замінити вміст (порожній рядок — очистити). */
  setMarkdown: (markdown: string) => void;
  focus: () => void;
};

export type RichTextEditorProps = {
  value: string;
  /** Автозбереження: після паузи в наборі й під час втрати фокуса. У режимі коментаря не задається. */
  onSave?: (markdown: string) => void;
  /** Кожна зміна тексту (для стану кнопки «Надіслати» й перевірки чутливих даних). */
  onChange?: (markdown: string) => void;
  /** ⌘/Ctrl+Enter: надіслати. Без нього комбінація завершує редагування (зберігає). */
  onSubmit?: () => void;
  onFocusChange?: (focused: boolean) => void;
  handle?: Ref<RichTextHandle>;
  label: string;
  placeholder?: string;
  readOnly?: boolean;
  users?: MentionUser[];
  /** Стан збереження під редактором (напр. «Зберігається…»). */
  status?: ReactNode;
  /** Затримка автозбереження, мс. */
  delay?: number;
  className?: string;
  /** Найменша висота поля введення, px (типово 88; низьке поле коментаря — 34). */
  minHeight?: number;
  /** Без власної рамки й тла: редактор стоїть усередині чужої рамки (поле коментаря). */
  bare?: boolean;
  /** Панель форматування: «focus» — лише під час редагування (типово), «always» — завжди. */
  toolbar?: 'focus' | 'always';
  /** Праворуч у панелі форматування (напр. кнопка «Надіслати»). */
  toolbarEnd?: ReactNode;
  /** Зовнішній блок попереджень/помилок під редактором. */
  footer?: ReactNode;
  'data-tour'?: string;
};

export function RichTextEditor({
  value,
  onSave,
  onChange,
  onSubmit,
  onFocusChange,
  handle,
  label,
  placeholder,
  readOnly,
  users = [],
  status,
  delay = 1200,
  className,
  minHeight = 88,
  bare = false,
  toolbar = 'focus',
  toolbarEnd,
  footer,
  ...rest
}: RichTextEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const saved = useRef(value);
  const dirty = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const [focused, setFocused] = useState(false);
  const [empty, setEmpty] = useState(!value.trim());
  const [trigger, setTrigger] = useState<Trigger | null>(null);
  const [active, setActive] = useState(0);
  const [link, setLink] = useState<{ rect: Rect; range: Range | null; url: string } | null>(null);
  const names = useMemo(() => users.map((u) => u.name), [users]);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  /** Вміст змінився: позначити, повідомити й запланувати автозбереження. */
  const touched = () => {
    dirty.current = true;
    const el = ref.current;
    if (!el) return;
    setEmpty(!(el.textContent ?? '').trim() && !el.querySelector('li, .rt-mention'));
    onChangeRef.current?.(htmlToMarkdown(el));
    schedule();
  };

  // Вміст із сервера — лише коли користувач не редагує (інакше не збиваємо курсор): під час редагування
  // нове значення відкладається й застосовується після втрати фокуса, якщо користувач нічого не змінив.
  const incoming = useRef<string | null>(null);
  const render = useCallback(
    (md: string) => {
      const el = ref.current;
      if (!el) return;
      // Порожній редактор починається з абзацу — набір і списки поводяться передбачувано.
      el.innerHTML = markdownToHtml(md, names) || '<p><br></p>';
      saved.current = md;
      dirty.current = false;
      setEmpty(!md.trim());
    },
    [names],
  );
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) {
      incoming.current = value;
      return;
    }
    if (value === saved.current && el.innerHTML) return;
    render(value);
  }, [value, render]);

  const flush = useCallback(() => {
    window.clearTimeout(timer.current);
    const el = ref.current;
    if (!el || !dirty.current) return;
    const md = htmlToMarkdown(el);
    dirty.current = false;
    if (md === saved.current.trim()) return;
    saved.current = md;
    onSaveRef.current?.(md);
  }, []);

  useImperativeHandle(
    handle,
    () => ({
      getMarkdown: () => (ref.current ? htmlToMarkdown(ref.current) : ''),
      setMarkdown: (md: string) => {
        window.clearTimeout(timer.current);
        render(md);
        onChangeRef.current?.(md);
      },
      focus: () => ref.current?.focus(),
    }),
    [render],
  );

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const schedule = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(flush, delay);
  };

  const exec = (cmd: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    touched();
  };

  /** Заголовок: звичайний текст → заголовок → підзаголовок → звичайний текст. */
  const cycleHeading = () => {
    const sel = window.getSelection();
    let node: Node | null = sel?.anchorNode ?? null;
    let current = 'P';
    while (node && node !== ref.current) {
      if (node instanceof HTMLElement && /^(H[1-6]|P|DIV|LI)$/.test(node.tagName)) {
        current = node.tagName;
        break;
      }
      node = node.parentNode;
    }
    if (current === 'LI') return; // у списку заголовків немає
    exec('formatBlock', current === 'H4' ? 'h5' : current === 'H5' ? 'p' : 'h4');
  };

  const removeTriggerText = (t: Trigger) => {
    const sel = window.getSelection();
    t.range.deleteContents();
    sel?.removeAllRanges();
    const r = document.createRange();
    r.setStart(t.range.startContainer, t.range.startOffset);
    r.collapse(true);
    sel?.addRange(r);
  };

  const insertMention = (u: MentionUser) => {
    const t = trigger;
    setTrigger(null);
    if (!t || !ref.current) return;
    ref.current.focus();
    removeTriggerText(t);
    const span = document.createElement('span');
    span.className = 'rt-mention';
    span.contentEditable = 'false';
    span.dataset.mention = u.name;
    span.textContent = `@${u.name}`;
    const space = document.createTextNode(' ');
    const sel = window.getSelection();
    const r = sel?.getRangeAt(0);
    if (!r) return;
    r.insertNode(space);
    r.insertNode(span);
    const after = document.createRange();
    after.setStartAfter(space);
    after.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(after);
    touched();
  };

  const openLink = () => {
    const sel = window.getSelection();
    const range = sel && sel.rangeCount && ref.current?.contains(sel.anchorNode) ? sel.getRangeAt(0).cloneRange() : null;
    const rect = range ? caretRect(range, ref.current!) : caretRect(document.createRange(), ref.current!);
    setLink({ rect, range, url: 'https://' });
  };

  const applyLink = () => {
    const l = link;
    setLink(null);
    if (!l || !ref.current) return;
    const url = l.url.trim();
    if (!SAFE_URL.test(url) || url === 'https://') return;
    ref.current.focus();
    const sel = window.getSelection();
    if (l.range) {
      sel?.removeAllRanges();
      sel?.addRange(l.range);
    }
    if (!l.range || l.range.collapsed) document.execCommand('insertHTML', false, `<a href="${esc(url)}" target="_blank" rel="noopener noreferrer">${esc(url)}</a>&nbsp;`);
    else document.execCommand('createLink', false, url);
    touched();
  };

  const commands: Command[] = [
    { id: 'ul', label: 'Маркований список', icon: <List className="size-4" aria-hidden />, run: () => exec('insertUnorderedList') },
    { id: 'ol', label: 'Нумерований список', icon: <ListOrdered className="size-4" aria-hidden />, run: () => exec('insertOrderedList') },
    { id: 'heading', label: 'Заголовок', icon: <Heading className="size-4" aria-hidden />, run: () => exec('formatBlock', 'h4') },
    { id: 'subheading', label: 'Підзаголовок', icon: <Heading className="size-3.5" aria-hidden />, run: () => exec('formatBlock', 'h5') },
    { id: 'bold', label: 'Жирний текст', hint: modKey('B'), icon: <Bold className="size-4" aria-hidden />, run: () => exec('bold') },
    { id: 'link', label: 'Посилання', hint: modKey('K'), icon: <Link2 className="size-4" aria-hidden />, run: () => openLink() },
    { id: 'mention', label: 'Згадати колегу', hint: '@', icon: <AtSign className="size-4" aria-hidden />, run: () => exec('insertText', '@') },
    { id: 'date', label: 'Сьогоднішня дата', icon: <CalendarDays className="size-4" aria-hidden />, run: () => exec('insertText', `${formatDateFull(todayISO())} `) },
    {
      id: 'result',
      label: 'Блок «Очікуваний результат»',
      icon: <Target className="size-4" aria-hidden />,
      run: () => exec('insertHTML', '<p><strong>Очікуваний результат:</strong>&nbsp;</p>'),
    },
    { id: 'docs', label: 'Блок «Документи»', icon: <Type className="size-4" aria-hidden />, run: () => exec('insertHTML', '<p><strong>Документи:</strong>&nbsp;</p>') },
  ];

  const mentionOptions = trigger?.kind === 'mention' ? users.filter((u) => matchesSearch(u.name, trigger.query)).slice(0, 8) : [];
  const commandOptions = trigger?.kind === 'command' ? commands.filter((c) => matchesSearch(c.label, trigger.query)) : [];
  const optionCount = trigger?.kind === 'mention' ? mentionOptions.length : commandOptions.length;

  const pick = (i: number) => {
    if (trigger?.kind === 'mention') {
      const u = mentionOptions[i];
      if (u) insertMention(u);
    } else if (trigger?.kind === 'command') {
      const c = commandOptions[i];
      const t = trigger;
      setTrigger(null);
      if (c && t) {
        ref.current?.focus();
        removeTriggerText(t);
        c.run();
      }
    }
  };

  // Esc у редакторі — вийти з редагування (зберегти), а не закрити картку задачі.
  useEscapeLayer(focused && !trigger && !link, () => ref.current?.blur());
  useEscapeLayer(!!trigger, () => setTrigger(null));

  const toolbarBtn = (title: string, icon: ReactNode, onClick: () => void, shortcut?: string) => (
    <button
      type="button"
      title={shortcut ? `${title} (${shortcut})` : title}
      aria-label={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="inline-flex size-7 items-center justify-center rounded-xs text-fg-2 hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-focus"
    >
      {icon}
    </button>
  );

  const listboxId = 'rt-suggest';

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="relative">
        <div
          ref={ref}
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          aria-readonly={readOnly || undefined}
          aria-autocomplete={readOnly ? undefined : 'list'}
          aria-controls={trigger ? listboxId : undefined}
          aria-activedescendant={trigger && optionCount ? `${listboxId}-${active}` : undefined}
          contentEditable={!readOnly}
          suppressContentEditableWarning
          spellCheck
          data-tour={rest['data-tour']}
          onFocus={() => {
            setFocused(true);
            onFocusChange?.(true);
            document.execCommand('defaultParagraphSeparator', false, 'p');
          }}
          onBlur={() => {
            setFocused(false);
            onFocusChange?.(false);
            // Клік по спливному меню/посиланню — зберігаємо після вибору.
            window.setTimeout(() => {
              if (document.activeElement === ref.current) return;
              flush();
              const next = incoming.current;
              incoming.current = null;
              // Значення з сервера, що прийшло під час редагування, — показуємо, якщо воно відрізняється від нашого.
              if (next !== null && !dirty.current && next.trim() !== saved.current.trim()) render(next);
            }, 0);
          }}
          onInput={() => {
            const el = ref.current;
            if (!el) return;
            touched();
            const t = detectTrigger(el);
            setTrigger(t);
            setActive(0);
          }}
          onPaste={(e) => {
            e.preventDefault();
            // Текст з Asana, Google Документів, Word чи пошти: структура зберігається, стилі й зображення — ні.
            const html = e.clipboardData.getData('text/html');
            const md = html ? pastedHtmlToMarkdown(html) : '';
            if (md) document.execCommand('insertHTML', false, markdownToHtml(md, names));
            else document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
            touched();
          }}
          onKeyDown={(e) => {
            const mod = e.metaKey || e.ctrlKey;
            if (trigger && optionCount) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((a) => (a + 1) % optionCount);
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((a) => (a - 1 + optionCount) % optionCount);
                return;
              }
              if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                pick(active);
                return;
              }
            }
            if (mod && e.code === 'KeyK') {
              e.preventDefault();
              openLink();
              return;
            }
            if (mod && !e.shiftKey && !e.altKey && (e.code === 'KeyB' || e.code === 'KeyI' || e.code === 'KeyU')) {
              e.preventDefault();
              exec(e.code === 'KeyB' ? 'bold' : e.code === 'KeyI' ? 'italic' : 'underline');
              return;
            }
            if (mod && e.shiftKey && !e.altKey && e.code === 'KeyX') {
              e.preventDefault();
              exec('strikeThrough');
              return;
            }
            if (mod && e.key === 'Enter') {
              e.preventDefault();
              if (onSubmit) onSubmit();
              else ref.current?.blur();
            }
          }}
          onKeyUp={(e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') setTrigger(ref.current ? detectTrigger(ref.current) : null);
          }}
          style={readOnly ? undefined : { minHeight }}
          className={cn(
            'rt prose-ers rounded-sm px-2.5 text-body text-fg outline-none',
            minHeight < 60 ? 'py-1.5' : 'py-2',
            !bare && 'border border-transparent',
            !readOnly && !bare && 'hover:border-line focus:border-line-input focus:bg-surface',
            readOnly && 'px-0',
          )}
        />
        {empty && !readOnly && (
          <p aria-hidden className={cn('pointer-events-none absolute left-2.5 text-body text-fg-3', minHeight < 60 ? 'top-1.5' : 'top-2', !bare && 'mt-px ml-px')}>
            {placeholder}
          </p>
        )}
      </div>
      {!readOnly && (toolbar === 'always' || focused || link) && (
        <div className="flex flex-wrap items-center gap-0.5" role="toolbar" aria-label="Форматування">
          {toolbarBtn('Заголовок', <Heading className="size-4" aria-hidden />, cycleHeading)}
          {toolbarBtn('Жирний', <Bold className="size-4" aria-hidden />, () => exec('bold'), modKey('B'))}
          {toolbarBtn('Курсив', <Italic className="size-4" aria-hidden />, () => exec('italic'), modKey('I'))}
          {toolbarBtn('Підкреслений', <Underline className="size-4" aria-hidden />, () => exec('underline'), modKey('U'))}
          {toolbarBtn('Закреслений', <Strikethrough className="size-4" aria-hidden />, () => exec('strikeThrough'), modKey('X', true))}
          {toolbarBtn('Маркований список', <List className="size-4" aria-hidden />, () => exec('insertUnorderedList'))}
          {toolbarBtn('Нумерований список', <ListOrdered className="size-4" aria-hidden />, () => exec('insertOrderedList'))}
          {toolbarBtn('Посилання', <Link2 className="size-4" aria-hidden />, openLink, modKey('K'))}
          {toolbarBtn('Згадати колегу', <AtSign className="size-4" aria-hidden />, () => {
            exec('insertText', '@');
            if (ref.current) setTrigger(detectTrigger(ref.current));
          })}
          {!toolbarEnd && (
            <span className="ml-2 hidden text-caption text-fg-3 sm:inline">
              <Kbd>/</Kbd> — вставки · <Kbd>@</Kbd> — згадати
            </span>
          )}
          <span className="ml-auto flex items-center gap-2 text-caption text-fg-3">
            {status}
            {toolbarEnd}
          </span>
        </div>
      )}
      {!readOnly && toolbar !== 'always' && !focused && !link && status && <span className="self-end text-caption text-fg-3">{status}</span>}
      {footer}
      <Popover
        open={!!trigger && optionCount > 0}
        onClose={() => setTrigger(null)}
        anchor={trigger?.rect ?? null}
        placement="bottom-start"
        role="listbox"
        id={listboxId}
        aria-label={trigger?.kind === 'mention' ? 'Кого згадати' : 'Вставити'}
        initialFocus="none"
        restoreFocus={false}
        ignoreOutside={(t) => !!ref.current?.contains(t)}
        className="w-64 p-1"
      >
        {trigger?.kind === 'mention'
          ? mentionOptions.map((u, i) => (
              <div
                key={u.id}
                id={`${listboxId}-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(i)}
                onMouseEnter={() => setActive(i)}
                className={cn('flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-body', i === active && 'bg-surface-2')}
              >
                <Avatar name={u.name} src={u.avatarUrl} size={20} />
                <span className="truncate">{u.name}</span>
              </div>
            ))
          : commandOptions.map((c, i) => (
              <div
                key={c.id}
                id={`${listboxId}-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(i)}
                onMouseEnter={() => setActive(i)}
                className={cn('flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-body', i === active && 'bg-surface-2')}
              >
                <span className="text-fg-3">{c.icon}</span>
                <span className="min-w-0 flex-1 truncate">{c.label}</span>
                {c.hint && <span className="text-caption text-fg-3">{c.hint}</span>}
              </div>
            ))}
      </Popover>
      <Popover open={!!link} onClose={() => setLink(null)} anchor={link?.rect ?? null} placement="bottom-start" aria-label="Посилання" className="w-80 p-2">
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            applyLink();
          }}
          className="flex items-center gap-1.5"
        >
          <Input
            data-autofocus
            aria-label="Адреса посилання"
            value={link?.url ?? ''}
            onChange={(e) => setLink((l) => (l ? { ...l, url: e.target.value } : l))}
            placeholder="https://…"
            inputMode="url"
            className="h-8 flex-1"
          />
          <button type="submit" className="h-8 rounded-sm bg-accent px-2.5 text-meta font-medium text-on-accent hover:bg-accent-hover">
            Додати
          </button>
        </form>
        {link && link.url.trim() && link.url !== 'https://' && !SAFE_URL.test(link.url.trim()) && (
          <p className="mt-1 text-caption text-danger-fg">Адреса має починатися з https:// або mailto:</p>
        )}
      </Popover>
    </div>
  );
}
